import { toast } from "react-toastify";

import dayjs from "dayjs";
import { nextCargoApi } from "../../baseApi";
import { truckApi } from "../truck/truck.api";
import {
  GetScheduling,
  GetSchedulingByDriver,
  GetSchedulingByDriverLicensePlate,
  ListSchedulingDeletedByDriver,
  SaveScheduling,
  SaveSchedulingClose,
  SaveSchedulingDelete
} from "./scheduling.types";

const schedulingApiRaw = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    saveSchedulingClose: build.mutation<SaveSchedulingClose["result"], SaveSchedulingClose["args"]>({
      query: (body) => ({
        url: "/scheduling/close",
        method: "POST",
        body
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          pending: "Encerrando atendimento do motorista...",
          success: "Atendimento do motorista encerrado com sucesso!",
          error: "Erro ao encerrar atendimento do motorista!"
        });
      }
    }),
    saveScheduling: build.mutation<SaveScheduling["result"], SaveScheduling["args"]>({
      query: (body) => ({
        url: "/scheduling/save",
        method: "POST",
        body
      })
    }),
    saveSchedulingDelete: build.mutation<SaveSchedulingDelete["result"], SaveSchedulingDelete["args"]>({
      query: ({ id }) => ({
        url: `/scheduling/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          pending: "Cancelando pedido...",
          success: "Pedido cancelado com sucesso!",
          error: "Erro ao cancelar pedido!"
        });
      }
    }),
    getScheduling: build.query<GetScheduling["result"], GetScheduling["args"]>({
      query: ({ id }) => `/scheduling/${id}`
    }),
    getSchedulingsByDriver: build.query<GetSchedulingByDriver["result"], GetSchedulingByDriver["args"]>({
      query: ({ id }) => `/scheduling/driver/${id}`
    }),
    getSchedulingByDriverLicensePlate: build.query<
      GetSchedulingByDriverLicensePlate["raw"],
      GetSchedulingByDriverLicensePlate["args"],
      GetSchedulingByDriverLicensePlate["result"]
    >({
      query: ({ driverId, licensePlate, companyId }) => ({
        url: `/scheduling/driver/${driverId}/licensePlate/${licensePlate}`,
        params: {
          companyId
        }
      }),
      transformResponse: (response) => {
        const sortedByExpirationDate = response.sort((a, b) => {
          const dateA = new Date(a.expirationDate || "").getTime();
          const dateB = new Date(b.expirationDate || "").getTime();
          return dateA - dateB;
        });

        const transformedResponse = sortedByExpirationDate.map((item) => ({
          ...item,
          truck: undefined
        }));

        return transformedResponse;
      },
      onQueryStarted: async (args, { queryFulfilled, dispatch }) => {
        toast.promise(queryFulfilled, {
          pending: "Buscando agendamento do motorista...",
          success: "Agendamento do motorista encontrado com sucesso!",
          error: "Erro ao buscar agendamento do motorista!"
        });

        const truckPromise = dispatch(truckApi.endpoints.getTruckByDriver.initiate({ id: args.driverId }));
        const promises = [truckPromise, queryFulfilled] as const;

        try {
          const [{ data: truckData }] = await Promise.all(promises);

          dispatch(
            schedulingApiRaw.util.updateQueryData("getSchedulingByDriverLicensePlate", args, (draft) => {
              draft.forEach((item) => {
                const truck = truckData?.find((truck) => truck.licensePlate === args.licensePlate);
                item.truck = truck ? truck : undefined;
                item.expirationDate = dayjs(item.expirationDate).add(1, "day").toISOString()
              });
            })
          );
        } catch (error) {
          console.error("Erro ao buscar agendamento do motorista:", error);
        }
      }
    }),
    listSchedulingDeletedByDriver: build.infiniteQuery<ListSchedulingDeletedByDriver["result"], ListSchedulingDeletedByDriver["args"], ListSchedulingDeletedByDriver["pageParam"]>({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 50 },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { id, ...params }, pageParam }) => ({
        url: `/schedulingDeleted/driver/${id}`,
        params: Object.assign({}, pageParam, params)
      })
    })
  })
});

export const schedulingApi = schedulingApiRaw.enhanceEndpoints({
  addTagTypes: ["SchedulingsByDriver", "SchedulingDeletedByDriver", "SchedulingByDriverLicensePlate"],
  endpoints: {
    saveSchedulingClose: {
      invalidatesTags: ["SyncExpedition", "SchedulingByDriverLicensePlate"]
    },
    saveScheduling: {
      invalidatesTags: ["SyncExpedition", "SchedulingByDriverLicensePlate"]
    },
    saveSchedulingDelete: {
      invalidatesTags: ["SyncExpedition", "SchedulingByDriverLicensePlate"]
    },
    getScheduling: {
      providesTags: (result, err, { id }) => [{ type: "SchedulingByDriverLicensePlate", id }]
    },
    getSchedulingsByDriver: {
      providesTags: (result, err, { id }) => [{ type: "SchedulingsByDriver", id }]
    },
    getSchedulingByDriverLicensePlate: {
      providesTags: (result, err, { driverId, licensePlate }) =>
        result ? result.map(({ id }) => ({ type: "SchedulingByDriverLicensePlate", id })) : [{ type: "SchedulingByDriverLicensePlate", id: `${driverId}-${licensePlate}` }]
    },
    listSchedulingDeletedByDriver: {
      providesTags: (result, err, { id }) => [{ type: "SchedulingDeletedByDriver", id }]
    }
  }
});
